import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Typography, Box, FormControlLabel, Checkbox, Tooltip, TextField, IconButton, Stack, createFilterOptions } from '@mui/material';
import { useSelector } from 'react-redux';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SimpleAutocompleteProps from '../../../../../reusable-components/autocomplete/SimpleAutocompleteProps';

IntacctBookingTypeForm.propTypes = {
  setFormValue: PropTypes.func,
  setRule: PropTypes.func,
  formValue: PropTypes.object,
};

const baseNetTerms = ["7", "14", "30", "45", "60", "75", "90"];

const ruleIds = {
  retryBillForGL: 10,
  glDateLastOfMonth: 12,
  applyLink: 13,
  applyBillTotal: 14,
  defaultDueDate: 15,
  applyPONumber: 18,
  applyBillDescription: 21,
};

const VENDOR_SEP = '-VendorId-';
const MAX_NET_DAYS = 365;

const toTerm = (value) => ({ value, label: `Net ${value}` });

// Accepts "21" or "Net 21" -> "21"; anything else -> null
const parseNetDays = (input) => {
  const match = String(input ?? '').trim().match(/^(?:net\s*)?(\d{1,3})$/i);
  if (!match) return null;
  const days = Number(match[1]);
  return days > 0 && days <= MAX_NET_DAYS ? String(days) : null;
};

const defaultFilter = createFilterOptions();

export default function IntacctBookingTypeForm({ formValue, setFormValue, setRule }) {
  const { data: { vendors } } = useSelector((state) => state.vendors);

  const rules = useMemo(() => {
    if (!formValue.rules) return {};
    return {
      retryBillForGL: formValue.rules?.find(rule => rule.id === ruleIds.retryBillForGL),
      glDateLastOfMonth: formValue.rules?.find(rule => rule.id === ruleIds.glDateLastOfMonth),
      applyLink: formValue.rules?.find(rule => rule.id === ruleIds.applyLink),
      applyBillTotal: formValue.rules?.find(rule => rule.id === ruleIds.applyBillTotal),
      applyBillDescription: formValue.rules?.find(rule => rule.id === ruleIds.applyBillDescription),
      applyPONumber: formValue.rules?.find(rule => rule.id === ruleIds.applyPONumber),
    };
  }, [formValue.rules]);

  const dueDateRules = useMemo(
    () => (formValue.rules ?? []).filter(r => r.id === ruleIds.defaultDueDate),
    [formValue.rules]
  );
  const defaultDueDateValue = dueDateRules.find(r => r.value && !r.value.includes(VENDOR_SEP))?.value ?? null;
  const vendorDueDates = useMemo(() => dueDateRules
    .filter(r => r.value?.includes(VENDOR_SEP))
    .map(r => {
      const [netDays, vendorId] = r.value.split(VENDOR_SEP);
      return { netDays, vendorId: Number(vendorId) };
    })
    .filter(r => r.netDays && !Number.isNaN(r.vendorId)),
    [dueDateRules]);
  const vendorTerm = useMemo(() => new Map(vendorDueDates.map(r => [r.vendorId, String(r.netDays)])), [vendorDueDates]);

  // Terms the user typed in this session (before saving). Once saved they live in the rules,
  // so on reload they're picked up from dueDateRules below.
  const [customTerms, setCustomTerms] = useState([]);

  // Full list = standard terms + any term already used on this booking type + newly added ones
  const allTerms = useMemo(() => {
    const values = new Set([
      ...baseNetTerms,
      ...(defaultDueDateValue ? [defaultDueDateValue] : []),
      ...vendorDueDates.map(r => String(r.netDays)),
      ...customTerms,
    ]);
    return [...values].sort((a, b) => Number(a) - Number(b)).map(toTerm);
  }, [defaultDueDateValue, vendorDueDates, customTerms]);

  const [pendingTerms, setPendingTerms] = useState([]);
  const termsInUse = allTerms.filter(t => vendorDueDates.some(r => String(r.netDays) === t.value) || pendingTerms.includes(t.value));
  // The "All other vendors" term can't be added as a vendor-specific group: it already applies to everyone
  const availableTerms = allTerms.filter(t => !termsInUse.includes(t) && t.value !== defaultDueDateValue);

  // Adds an 'Add "Net X"' option when the typed value is a valid term that isn't in the list yet
  const filterWithAdd = (options, params) => {
    const filtered = defaultFilter(options, params);
    const days = parseNetDays(params.inputValue);
    if (days && !allTerms.some(t => t.value === days))
      filtered.push({ value: days, label: `Add "Net ${days}"`, isNew: true });
    return filtered;
  };

  // Returns the picked term value and registers it in the list if it's new
  const pickTerm = (option) => {
    if (!option) return null;
    if (option.isNew) setCustomTerms(terms => (terms.includes(option.value) ? terms : [...terms, option.value]));
    return option.value;
  };

  const writeDueDateRules = (defaultValue, rows) => setFormValue(value => ({
    ...value,
    rules: [
      ...(value.rules ?? []).filter(r => r.id !== ruleIds.defaultDueDate),
      ...(defaultValue ? [{ id: ruleIds.defaultDueDate, value: defaultValue }] : []),
      ...rows.map(r => ({ id: ruleIds.defaultDueDate, value: `${r.netDays}${VENDOR_SEP}${r.vendorId}` })),
    ],
  }));

  // Setting the default to a term that already has a vendor group makes that group redundant -> drop it
  const setDefaultDueDate = (value) => {
    setPendingTerms(terms => terms.filter(t => t !== value));
    writeDueDateRules(value, vendorDueDates.filter(r => String(r.netDays) !== value));
  };
  const setVendorDueDates = (rows) => writeDueDateRules(defaultDueDateValue, rows);

  const setVendorsForTerm = (termValue, selected) => {
    const selectedIds = new Set(selected.map(v => v.id));
    setVendorDueDates([
      ...vendorDueDates.filter(r => String(r.netDays) !== termValue && !selectedIds.has(r.vendorId)),
      ...selected.map(v => ({ vendorId: v.id, netDays: termValue })),
    ]);
  };

  const removeTerm = (termValue) => {
    setPendingTerms(terms => terms.filter(t => t !== termValue));
    setVendorDueDates(vendorDueDates.filter(r => String(r.netDays) !== termValue));
  };

  return <>
    <Tooltip title="The specific folder on Intacct to upload the attached PDFs to." arrow placement="left">
      <Box>
        <Typography variant="subtitle1">
          Invoice PDF folder
        </Typography>
        <TextField
          size="small"
          value={formValue.attachmentFolder ?? ""}
          onChange={(e) => setFormValue(value => ({ ...value, attachmentFolder: e.target.value }))}
          fullWidth
        />
      </Box>
    </Tooltip>
    <Tooltip title="Used for authentication. Applies an identifying extra default segment to the customer id before sending the authentication request. If this is filled there must be at least one facility attached to this booking type for the connection test to work." arrow placement="left">
      <Box>
        <Typography variant="subtitle1">
          Extra identifying segment for authentication
        </Typography>
        <TextField
          size="small"
          value={formValue.extraIdentifyingSegment ?? ""}
          onChange={(e) => setFormValue(value => ({ ...value, extraIdentifyingSegment: e.target.value }))}
          fullWidth
        />
      </Box>
    </Tooltip>
    <Tooltip title="Sends direct link to invoice into Intacct. Note not all Intacct instances will allow this. The value must be a direct match to their system." arrow placement="left">
      <Box>
        <Typography variant="subtitle1">
          Send Link
        </Typography>
        <TextField
          size="small"
          value={rules.applyLink?.value ?? ""}
          onChange={(e) => setRule(ruleIds.applyLink, e.target.value ? { id: ruleIds.applyLink, value: e.target.value } : null)}
          fullWidth
        />
      </Box>
    </Tooltip>
    <Box>
      <Typography variant="subtitle1">
        Default due date
      </Typography>

      <Tooltip title="Applies to every invoice on this booking type whose vendor isn't assigned to a term below. Overrides all other inputs. Type a number to add a custom term." arrow placement="left">
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" sx={{ width: 120, flexShrink: 0 }}>All other vendors</Typography>
          <Autocomplete
            {...SimpleAutocompleteProps}
            sx={{ flex: 1 }}
            options={allTerms}
            filterOptions={filterWithAdd}
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            getOptionLabel={(option) => option?.label ?? ""}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            value={allTerms.find(term => term.value === defaultDueDateValue) ?? null}
            onChange={(e, val) => setDefaultDueDate(pickTerm(val))}
          />
          <Box sx={{ width: 34, flexShrink: 0 }} />
        </Stack>
      </Tooltip>

      <Stack spacing={1} sx={{ mt: 1 }}>
        {termsInUse.map(term => {
          const selected = vendors.filter(v => vendorTerm.get(v.id) === term.value);
          return (
            <Stack key={term.value} direction="row" spacing={1} alignItems="flex-start">
              <Typography variant="body2" sx={{ width: 120, flexShrink: 0, pt: 1 }}>
                {term.label} <Typography component="span" variant="caption" color="text.secondary">({selected.length})</Typography>
              </Typography>
              <Autocomplete
                multiple
                size="small"
                sx={{ flex: 1 }}
                limitTags={3}
                disableCloseOnSelect
                options={vendors}
                getOptionLabel={(option) => option?.name ?? ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selected}
                onChange={(e, val) => setVendorsForTerm(term.value, val)}
                renderOption={(props, option, { selected: isSelected }) => {
                  const { key, ...optionProps } = props;
                  const currentTerm = vendorTerm.get(option.id);
                  return (
                    <li key={key} {...optionProps}>
                      <Checkbox size="small" checked={isSelected} sx={{ mr: 1, p: 0.5 }} />
                      {option.name}
                      {currentTerm && currentTerm !== term.value && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', pl: 1 }}>
                          on Net {currentTerm} — will move
                        </Typography>
                      )}
                    </li>
                  );
                }}
                renderInput={(params) => <TextField {...params} placeholder={selected.length ? "" : "Select vendors"} />}
              />
              <IconButton size="small" sx={{ mt: 0.5 }} onClick={() => removeTerm(term.value)} aria-label={`Remove ${term.label} vendors`}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          );
        })}

        <Autocomplete
          size="small"
          sx={{ width: 240 }}
          options={availableTerms}
          filterOptions={filterWithAdd}
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          getOptionLabel={(option) => option?.label ?? ""}
          value={null}
          blurOnSelect
          onChange={(e, val) => {
            const value = pickTerm(val);
            if (value) setPendingTerms(terms => (terms.includes(value) ? terms : [...terms, value]));
          }}
          renderInput={(params) => <TextField {...params} placeholder="+ Add vendor-specific terms" />}
        />
      </Stack>
    </Box>

    <Tooltip title="Used for authentication. Sends the customer id on the authentication request. (not needed if the extra identifying segment is selected)" arrow placement="left">
      <FormControlLabel disabled={!!formValue.extraIdentifyingSegment} control={<Checkbox checked={formValue.hasEntityId ?? false}
        onChange={(e) => setFormValue(value => ({ ...value, hasEntityId: e.target.checked }))} />} label="Uses direct customer ids" />
    </Tooltip>
    <Tooltip title="Used for authentication. Sends an extra customer sub id on the authentication request." arrow placement="left">
      <FormControlLabel disabled={!!formValue.extraIdentifyingSegment && !formValue.hasEntityId} control={<Checkbox checked={formValue.useSubFacilityAccountId ?? false}
        onChange={(e) => setFormValue(value => ({ ...value, useSubFacilityAccountId: e.target.checked }))} />} label="Uses customer sub-accounts" />
    </Tooltip>
    <FormControlLabel control={<Checkbox checked={formValue.hasDepartmentCode ?? false}
      onChange={(e) => setFormValue(value => ({ ...value, hasDepartmentCode: e.target.checked }))} />} label="Uses department codes for GLs" />
    <Tooltip title="Retries the upload if the GL date is invalid. Attempts to post to the first of the month until it finds a valid date." arrow placement="left">
      <FormControlLabel control={<Checkbox checked={rules.retryBillForGL ?? false} onChange={(e) =>
        setRule(ruleIds.retryBillForGL, e.target.checked ? { id: ruleIds.retryBillForGL } : null)} />} label="Retry invoice upload for GL dates" />
    </Tooltip>
    <Tooltip title="Always initially post the GL date to last day of month." arrow placement="left">
      <FormControlLabel control={<Checkbox checked={rules.glDateLastOfMonth ?? false} onChange={(e) =>
        setRule(ruleIds.glDateLastOfMonth, e.target.checked ? { id: ruleIds.glDateLastOfMonth } : null)} />} label="Set GL date to last of month" />
    </Tooltip>
    <Tooltip title="Sends the bill/invoice total to Intacct (this is only for an Intacct instance that has the BILL_TOTAL smart rule)" arrow placement="left">
      <FormControlLabel control={<Checkbox checked={rules.applyBillTotal ?? false} onChange={(e) =>
        setRule(ruleIds.applyBillTotal, e.target.checked ? { id: ruleIds.applyBillTotal } : null)} />} label='Send "Bill Total" on invoice' />
    </Tooltip>
    <Tooltip title="Sends a description (the PO number) to Intacct (this is only for an Intacct instance that has the AP_BILL_DESCRIPTION smart rule)" arrow placement="left">
      <FormControlLabel control={<Checkbox checked={rules.applyBillDescription ?? false} onChange={(e) =>
        setRule(ruleIds.applyBillDescription, e.target.checked ? { id: ruleIds.applyBillDescription } : null)} />} label='Send "Description" on invoice' />
    </Tooltip>
  </>;
}
